<template>
  <div class="premium-input-wrap">
    <!-- Label -->
    <label v-if="label" class="premium-label">{{ label }}</label>

    <!-- ── TEXTAREA (multiline / resizable) ── -->
    <v-textarea
      v-if="multiline"
      v-model="internalValue"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      outlined
      dense
      hide-details="auto"
      :rows="rows"
      :auto-grow="autoGrow"
      :no-resize="!resizable"
      class="premium-input premium-textarea"
      :class="{ 'is-dark': isDark }"
      v-bind="$attrs"
      :error-messages="error"
      :error="!!error"
      v-on="listenersWithoutInput"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-textarea>

    <!-- ── TEXT FIELD (default) ── -->
    <v-text-field
      v-else
      v-model="internalValue"
      :type="inputType === 'password' && !showPassword ? 'password' : (inputType === 'password' ? 'text' : inputType)"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      :append-icon="inputType === 'password' ? (showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline') : appendIcon"
      outlined
      dense
      hide-details="auto"
      class="premium-input"
      :class="{ 'is-dark': isDark }"
      v-bind="$attrs"
      :error-messages="error"
      :error="!!error"
      @click:append="inputType === 'password' ? showPassword = !showPassword : null"
      v-on="listenersWithoutInput"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'PremiumInput',
  inheritAttrs: false,

  props: {
    value:       { default: '' },
    label:       { type: String,  default: '' },
    placeholder: { type: String,  default: '' },
    type:        { type: String,  default: 'text' },
    icon:        { type: String,  default: '' },
    appendIcon:  { type: String,  default: '' },
    error:       { type: [String, Array], default: '' },

    // ── new textarea props ──
    multiline:   { type: Boolean, default: false }, // switch to textarea
    rows:        { type: [Number, String], default: 2 }, // initial row height
    autoGrow:    { type: Boolean, default: true  }, // grow with content
    resizable:   { type: Boolean, default: true  }, // show resize handle
  },

  data() {
    return {
      showPassword: false,
      inputType: this.type,
    };
  },

  computed: {
    ...mapState('theme', ['isDark', 'primaryColor']),

    internalValue: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },

    /**
     * Exclude 'input' from $listeners so that internalValue's setter is the
     * sole handler for the input event. Passing 'input' via v-on="$listeners"
     * AND having internalValue emit 'input' causes a double-emit that can
     * reset the field value (the parent's raw DOM event handler fires with
     * the Event object instead of the typed string).
     */
    listenersWithoutInput() {
      const listeners = { ...this.$listeners };
      delete listeners.input;
      return listeners;
    },
  },

  watch: {
    type(newType) { this.inputType = newType; },
  },
};
</script>

<style scoped>
.premium-input-wrap {
  margin-bottom: 2px;
}

.premium-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6B7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}

/* ── shared fieldset / border ── */
.premium-input >>> fieldset {
  border-width: 1px !important;
  border-color: var(--border, rgba(0,0,0,0.1)) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.premium-input.v-input--is-focused >>> fieldset {
  border-color: var(--brand, #10B981) !important;
  border-width: 1.5px !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12) !important;
}

/* ── text-field height ── */
.premium-input:not(.premium-textarea) >>> .v-input__control {
  min-height: 46px !important;
}

/* ── shared text style ── */
.premium-input >>> input,
.premium-input >>> textarea {
  font-size: 13.5px !important;
  font-weight: 500 !important;
  color: var(--tx, #111827) !important;
}

/* ── textarea: user-resizable via drag handle ── */
.premium-textarea >>> textarea {
  resize: vertical !important;
  min-height: 46px !important;
  line-height: 1.6 !important;
  padding-top: 10px !important;
  padding-bottom: 8px !important;
}

/* ── dark mode ── */
.is-dark.premium-input >>> input,
.is-dark.premium-input >>> textarea {
  color: #F1F5F9 !important;
}

.is-dark.premium-input >>> fieldset {
  border-color: rgba(255,255,255,0.1) !important;
}

/* ── icon ── */
.premium-input >>> .v-icon {
  font-size: 18px !important;
  color: var(--tx3, #94A3B8) !important;
  transition: color 0.2s ease;
}

.premium-input.v-input--is-focused >>> .v-icon {
  color: var(--brand, #10B981) !important;
}

/* ── icon alignment when inside a textarea ── */
.premium-textarea >>> .v-input__prepend-inner {
  margin-top: 12px !important;
}

/* ── error state ── */
.premium-input.v-input--has-state.error--text >>> fieldset {
  border-color: #EF4444 !important;
}
</style>
